// src/core/contexts/ServiceContext.tsx

import React, { createContext, useContext, ReactNode } from 'react';
import { container, SERVICE_IDENTIFIERS } from '@core/di/ServiceContainer.ts';
import { IErrorHandler, INotificationService, IAnalyticsService } from '@core/interfaces';

/**
 * Service Context - Provides access to services via DI container
 */
interface ServiceContextType {
    errorHandler: IErrorHandler;
    analytics: IAnalyticsService;
    notification: INotificationService;
}

const ServiceContext = createContext<ServiceContextType | undefined>(undefined);

interface ServiceProviderProps {
    children: ReactNode;
}

/**
 * ServiceProvider - Provides services from DI container to React components
 *
 * Services must be registered in index.tsx BEFORE this provider is used
 */
export const ServiceProvider: React.FC<ServiceProviderProps> = ({ children }) => {
    // Get services from DI container
    const errorHandler = getErrorHandlerService();
    const analytics = getAnalyticsService();
    const notification = getNotificationService();

    console.log('✅ ServiceProvider: Services loaded from DI container');

    const value: ServiceContextType = {
        errorHandler,
        analytics,
        notification
    };

    return <ServiceContext.Provider value={value}>{children}</ServiceContext.Provider>;
};

/**
 * Get Analytics service from DI container
 * @returns IAnalyticsService instance
 */
export function getAnalyticsService(): IAnalyticsService {
    return container.resolve<IAnalyticsService>(SERVICE_IDENTIFIERS.ANALYTICS);
}

/**
 * Get Notification service from DI container
 * @returns INotificationService instance
 */
export function getNotificationService(): INotificationService {
    return container.resolve<INotificationService>(SERVICE_IDENTIFIERS.NOTIFICATION);
}

export function getErrorHandlerService(): IErrorHandler {
    return container.resolve<IErrorHandler>(SERVICE_IDENTIFIERS.ERROR_HANDLER);
}

/**
 * useServices - Hook to access all services
 */
export const useServices = (): ServiceContextType => {
    const context = useContext(ServiceContext);

    if (context === undefined) {
        throw new Error('useServices must be used within a ServiceProvider');
    }

    return context;
};

/**
 * useErrorHandler - Hook to access error handler service
 */
export const useErrorHandler = (): IErrorHandler => {
    const { errorHandler } = useServices();
    return errorHandler;
};

/**
 * useAnalytics - Hook to access analytics service
 */
export const useAnalytics = (): IAnalyticsService => {
    const { analytics } = useServices();
    return analytics;
};

/**
 * useNotification - Hook to access notification service
 */
export const useNotification = (): INotificationService => {
    const { notification } = useServices();
    return notification;
};

/**
 * withServices - HOC to inject services into class components
 */
export interface WithServicesProps {
    services: ServiceContextType;
}

export const withServices = <P extends object>(Component: React.ComponentType<P & WithServicesProps>) => {
    return (props: P) => {
        const services = useServices();
        return <Component {...props} services={services} />;
    };
};
